import { useState } from 'react';
import { X } from 'lucide-react';
import { useCookieConsentContext } from '../../context/CookieConsentContext';

interface CategoryRowProps {
  label: string;
  description: string;
  locked?: boolean;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function CategoryRow({ label, description, locked, checked, onChange }: CategoryRowProps) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-surface-300 last:border-0">
      <div className="flex-1">
        <p className="text-sm font-semibold text-surface-50">{label}</p>
        <p className="text-xs text-surface-100/50 mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={locked}
        onClick={() => !locked && onChange(!checked)}
        className={[
          'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none',
          locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          checked ? 'bg-primary-400' : 'bg-surface-500',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

export function CookiePreferences() {
  const { consent, preferencesOpen, closePreferences, updateConsent, acceptAll } = useCookieConsentContext();

  const [perf] = useState(consent?.performance ?? true);
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [advertising, setAdvertising] = useState(consent?.advertising ?? true);

  if (!preferencesOpen) return null;

  function save() {
    updateConsent({ performance: perf, analytics, advertising });
    closePreferences();
  }

  function handleAcceptAll() {
    acceptAll();
    closePreferences();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-800 rounded-2xl border border-surface-300 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-300">
          <h2 className="text-base font-bold text-surface-50">Cookie Preferences</h2>
          <button
            onClick={closePreferences}
            className="text-surface-100/50 hover:text-surface-50 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6">
          <CategoryRow
            label="Essential"
            description="Required for the app to function — API requests, payments, fonts. Cannot be disabled."
            locked
            checked
            onChange={() => {}}
          />
          <CategoryRow
            label="Performance & Functionality"
            description="Required for video embeds (YouTube) and interactive maps (Google Maps). Cannot be disabled."
            locked
            checked
            onChange={() => {}}
          />
          <CategoryRow
            label="Analytics"
            description="Helps us understand how the site is used. No analytics service is currently active."
            checked={analytics}
            onChange={setAnalytics}
          />
          <CategoryRow
            label="Advertising"
            description="Used by embedded YouTube videos to serve relevant ads."
            checked={advertising}
            onChange={setAdvertising}
          />
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-surface-300">
          <button
            onClick={handleAcceptAll}
            className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            Accept All
          </button>
          <button
            onClick={save}
            className="px-5 py-2 text-sm font-semibold text-surface-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-colors"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
